/**
 * Clerk satellite resolution - PARTNER MODE only.
 *
 * Thin facade over the middleware-injected partner context
 * (`./current-partner.ts`). Used on the shared deploy to point the Clerk SDK
 * at `clerk.<partner-root-domain>` (a satellite of the shared primary Clerk
 * instance) when the current hostname belongs to a registered partner.
 * Never reads the full partner registry: a page only knows its own partner
 * (partner hosts) or the partner a sign-in bounce came from (Tagada hosts).
 *
 * NOT used by whitelabel deploys (apptics, fmj) - those run their own
 * primary Clerk instance per deploy via `NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY`
 * in `.env.<brand>.prod`, with no satellite involvement.
 *
 * Satellite constraint reminder: Clerk rejects sign-in CREATION operations
 * (signIn.create, OAuth start, OTP send) on satellites. Session CONSUMPTION
 * (existing-cookie handshake) works transparently. That's why a satellite
 * /sign-in bounces to the primary - see src/app/sign-in.tsx.
 */

import { currentPartner, knownPartnerByHost, knownPartnerOrigins } from './current-partner';

export const PRIMARY_APP_ORIGIN = 'https://app.tagada.io';

/**
 * App (+ dashboard) origins of the partners this page knows: the host's own
 * partner on a partner host, the partner a sign-in bounce came from on a
 * Tagada host. Used for Clerk's `allowedRedirectOrigins` and as the
 * partner part of the `redirect_url` allowlist on primary /sign-in.
 * A function: the redirect partner can be resolved after module load
 * (`resolveRedirectPartnerFromHash`).
 */
export const partnerOrigins = (): readonly string[] => knownPartnerOrigins();

/**
 * TagadaPay-owned satellite origins that the primary CRM is allowed to
 * bounce sign-in / sign-up traffic back to. These are NOT partner
 * brands — they're additional Clerk-satellite hosts owned by us where
 * the legacy `tagadapay-app` SPA still lives (the partner-facing
 * dashboard, the docs page, etc.). Treated as `redirect_url`
 * targets just like `partnerOrigins()` but always render with the
 * default tagadapay brand on the primary's sign-in page.
 *
 * New TagadaPay-owned satellites MUST be added to the hardcoded
 * default list below so they survive a fresh Vercel project (the
 * `safeSignInRedirectUrl` allowlist silently rejects unknown origins
 * and the user gets stranded on `/` after sign-in).
 *
 * Override per env via the comma-separated env var
 * `VITE_TAGADAPAY_SATELLITE_ORIGINS`. Per environment:
 *   - prod  (`app.tagada.io`)  → defaults below cover it
 *   - dev   (`app.tagada.dev`) → `https://dashboard.tagadapay.dev,https://paydash.tagada.dev`, …
 *   - local                    → add your local satellite host(s) as needed
 * Without the dev origin the post-sign-in `redirect_url` check rejects
 * `https://dashboard.tagadapay.dev/...` and the user lands on `/`
 * instead of bouncing back to the satellite.
 */
/**
 * Tagada-owned satellites that we always want to bounce sign-in/sign-up
 * back to, regardless of build-time env. Adding new tagada-root
 * satellites (e.g. paydash.tagada.io = payment-error-dashboard,
 * payment.tagada.io = MOR dashboard) here
 * avoids the trap where a satellite forwards `?redirect_url=` to the
 * primary, the primary doesn't recognize the origin, drops it, and
 * lands the user on `/` (CRM home) instead of returning them to the
 * satellite — which manifests as an infinite primary↔satellite loop
 * when the satellite's ProtectedRoute keeps re-redirecting to /sign-in.
 *
 * Note: there's a one-time post-install audit task to keep this list
 * in sync with the satellite domains registered on the shared Clerk
 * instance (Clerk dashboard → Domains → satellites). Hardcoding here
 * keeps the build deterministic — env-var overrides via
 * VITE_TAGADAPAY_SATELLITE_ORIGINS are merged in below.
 */
const DEFAULT_TAGADAPAY_SATELLITES = [
  'https://dashboard.tagadapay.io',
  'https://dashboard.tagadapay.dev',
  'https://payment.tagada.io',
  'https://paydash.tagada.io',
  'https://paydash.tagada.dev',
  'http://localhost:3011',
];

const RAW_TAGADAPAY_SATELLITES =
  (import.meta.env?.VITE_TAGADAPAY_SATELLITE_ORIGINS as string | undefined) ?? '';
export const TAGADAPAY_SATELLITE_ORIGINS: readonly string[] = Array.from(
  new Set([
    ...DEFAULT_TAGADAPAY_SATELLITES,
    ...(RAW_TAGADAPAY_SATELLITES
      ? RAW_TAGADAPAY_SATELLITES.split(',').map((s) => s.trim()).filter(Boolean)
      : []),
  ]),
);

/**
 * Full origin allowlist for the `redirect_url` query param on the
 * primary's sign-in / sign-up pages. Combines partner satellite hosts
 * with the TagadaPay-owned satellites above.
 */
export const allowedSignInRedirectOrigins = (): readonly string[] => [
  ...partnerOrigins(),
  ...TAGADAPAY_SATELLITE_ORIGINS,
];

/**
 * Decide if `url` (typically `redirect_url` from sign-in / sign-up
 * query params) is a safe cross-origin redirect target. Returns the
 * canonical URL string when allowed, null otherwise.
 */
export function safeSignInRedirectUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (allowedSignInRedirectOrigins().includes(u.origin)) return u.toString();
  } catch {
    return null;
  }
  return null;
}

/**
 * `safeSignInRedirectUrl` narrowed to the origins of the partner serving
 * `currentHost` (its app and dashboard hosts), so one partner's sign-in
 * page can never bounce a user to another brand. Null off partner hosts.
 */
export function safePartnerRedirectUrl(
  url: string | null | undefined,
  currentHost: string,
): string | null {
  const safe = safeSignInRedirectUrl(url);
  if (!safe) return null;
  const partner = knownPartnerByHost(currentHost);
  if (!partner || knownPartnerByHost(new URL(safe).hostname) !== partner) return null;
  return safe;
}

export interface SatelliteConfig {
  /** Clerk FAPI hostname (CNAME -> frontend-api.clerk.services). */
  domain: string;
}

/**
 * If the current window is on a partner host, return its satellite config.
 * Returns null on the primary (app.tagada.io), on Vercel previews, and
 * during SSR (no window).
 */
export function resolveSatellite(): SatelliteConfig | null {
  if (typeof window === 'undefined') return null;
  const partner = currentPartner();
  return partner ? { domain: partner.clerkFapi } : null;
}

export function isSatelliteHost(): boolean {
  return resolveSatellite() !== null;
}
